import type { WritableComputedRef } from 'vue'
import { createI18n } from 'vue-i18n'
import {
  APPLICATION_BOOTSTRAP_ELEMENT_ID,
  FALLBACK_LOCALE,
  parseApplicationBootstrap,
  resolveSupportedLocaleFromLanguagePreferences,
  SUPPORTED_LOCALES,
  type ApplicationBootstrap,
  type SupportedLocale,
} from '@threadline/shared'
import englishUnitedStatesCatalog from '../../../shared/i18n/en-US.yaml'
import portugueseBrazilCatalog from '../../../shared/i18n/pt-BR.yaml'
import spanishLatinAmericaCatalog from '../../../shared/i18n/es-419.yaml'

const localizationCatalogs = {
  'en-US': englishUnitedStatesCatalog,
  'pt-BR': portugueseBrazilCatalog,
  'es-419': spanishLatinAmericaCatalog,
}

/**
 * Whoever served the shell wrote this deployment's branding into it, so the language is
 * known before the first paint and without waiting for an API call. It is absent in unit
 * tests, which mount components without a shell, and there the browser decides as always.
 */
const applicationBootstrap: ApplicationBootstrap | null = readApplicationBootstrapFromDocument()

function readApplicationBootstrapFromDocument(): ApplicationBootstrap | null {
  if (typeof document === 'undefined') {
    return null
  }
  const bootstrapElement = document.getElementById(APPLICATION_BOOTSTRAP_ELEMENT_ID)
  if (bootstrapElement === null) {
    return null
  }
  return parseApplicationBootstrap(bootstrapElement.textContent ?? '')
}

/** The single language this deployment serves, or `null` when it serves all of them. */
export function resolveDeploymentLocale(): SupportedLocale | null {
  return applicationBootstrap?.deploymentLocale ?? null
}

export function isMultilingualDeployment(): boolean {
  return resolveDeploymentLocale() === null
}

/**
 * The language the interface starts in. A deployment that serves one language always uses
 * it; only a multilingual deployment asks the browser.
 */
export function resolveInitialLocale(): SupportedLocale {
  return resolveDeploymentLocale() ?? detectBrowserLocale()
}

/** Browser preferences resolved through the very same rules the backend applies. */
export function detectBrowserLocale(): SupportedLocale {
  const browserLanguageTags =
    typeof navigator !== 'undefined' && navigator.languages.length > 0
      ? [...navigator.languages]
      : [typeof navigator === 'undefined' ? FALLBACK_LOCALE : navigator.language]
  return resolveSupportedLocaleFromLanguagePreferences(browserLanguageTags)
}

export const internationalization = createI18n({
  legacy: false,
  globalInjection: true,
  locale: resolveInitialLocale(),
  fallbackLocale: FALLBACK_LOCALE,
  messages: localizationCatalogs,
})

/** With the Composition API the active locale is a writable ref on the global composer. */
const activeLocaleReference =
  internationalization.global.locale as unknown as WritableComputedRef<SupportedLocale>

/**
 * Applies the language, and names the browser tab after the account it belongs to.
 *
 * Two tabs can hold two different accounts at once, and the tab strip is where somebody
 * tells them apart — so the title carries the handle of whichever account *this* tab is
 * acting as. The separator is in the catalogs rather than here, because how a title reads
 * is a decision each language gets to make.
 *
 * A multilingual deployment has no single product name either, so both halves follow the
 * language the person is actually reading.
 */
export function applyActiveLocale(
  activeLocale: SupportedLocale,
  accountHandle: string | null = null,
): void {
  activeLocaleReference.value = activeLocale
  if (typeof document === 'undefined') {
    return
  }

  document.documentElement.setAttribute('lang', activeLocale)
  const productName = internationalization.global.t('product.name')
  document.title =
    accountHandle === null || accountHandle.length === 0
      ? // With nobody signed in there is nothing to tell two tabs apart by, so the tab can
        // afford to say what the product is instead.
        internationalization.global.t('product.windowTitleWithTagline', {
          productName,
          tagline: internationalization.global.t('product.tagline'),
        })
      : internationalization.global.t('product.windowTitleWithAccount', {
          productName,
          accountHandle,
        })
}

export function listSupportedLocaleOptions(): Array<{
  locale: SupportedLocale
  localizationKey: string
}> {
  const localizationKeysByLocale: Record<SupportedLocale, string> = {
    'en-US': 'locale.englishUnitedStates',
    'pt-BR': 'locale.portugueseBrazil',
    'es-419': 'locale.spanishLatinAmerica',
  }
  return SUPPORTED_LOCALES.map((supportedLocale) => ({
    locale: supportedLocale,
    localizationKey: localizationKeysByLocale[supportedLocale],
  }))
}
